import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy, Calendar, Users, ArrowRight, BadgeCheck } from 'lucide-react';
import { cn, formatDisplayDate, formatScore, scoreColor } from '@/lib/utils';

interface RaceRankingCardProps {
    raceId: string;
    raceName: string;
    bettingDeadline: string | Date;
    isOfficial: boolean;
    totalScore: number;
    rank: number;
    totalBettors: number;
}

export function RaceRankingCard({
    raceId,
    raceName,
    bettingDeadline,
    isOfficial,
    totalScore,
    rank,
    totalBettors,
}: RaceRankingCardProps) {
    const { t } = useTranslation('bets');

    const isWinner = rank === 1;

    return (
        <Link
            to={`/races/${raceId}/leaderboard`}
            className={cn(
                "group block bg-white p-4 rounded-2xl border shadow-sm hover:shadow-md transition-all",
                isWinner
                    ? "border-amber-200 hover:border-amber-300"
                    : "border-slate-100 hover:border-emerald-100"
            )}
        >
            <div className="flex items-start gap-3">
                <div className={cn(
                    "w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors",
                    isWinner
                        ? "bg-amber-50 group-hover:bg-amber-100"
                        : "bg-slate-50 group-hover:bg-slate-100"
                )}>
                    <Trophy size={20} className={isWinner ? "text-amber-500" : "text-slate-400"} />
                </div>

                <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900 group-hover:text-emerald-700 transition-colors flex items-center gap-1">
                        <span className="truncate">{raceName}</span>
                        {isOfficial && <BadgeCheck size={16} className="text-amber-500 flex-shrink-0" />}
                    </h3>

                    <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                        <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {formatDisplayDate(bettingDeadline).split('à')[0]}
                        </span>
                        <span className="flex items-center gap-1">
                            <Users size={12} />
                            {t('ranking.byRace.bettors', { count: totalBettors })}
                        </span>
                    </div>

                    <div className="flex items-center gap-3 mt-2">
                        <span className={cn(
                            "px-2.5 py-1 rounded-lg text-xs font-bold",
                            isWinner
                                ? "bg-amber-50 text-amber-700"
                                : "bg-emerald-50 text-emerald-700"
                        )}>
                            {t('ranking.byRace.yourRank', { rank, total: totalBettors })}
                        </span>
                        <span className={cn("text-sm font-black", scoreColor(totalScore, "text-slate-700"))}>
                            {formatScore(totalScore)} {t('ranking.global.pts')}
                        </span>
                        {isWinner && (
                            <span className="text-xs font-bold text-amber-600">
                                {t('ranking.byRace.winner')}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center text-emerald-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all mt-3">
                    <ArrowRight size={16} />
                </div>
            </div>
        </Link>
    );
}
